<template>
  <div class="account-sidebar">
    <!-- 1. DEADLINES / TAKEN -->
    <v-card v-if="!hideDeadlines" class="mb-4 sidebar-card">
      <v-card-title
        class="section-header d-flex align-center flex-nowrap"
        :class="
          pastDeadlinesCount > 0 ? 'bg-red-lighten-5' : 'bg-green-lighten-5'
        "
      >
        <v-icon
          size="20"
          :color="pastDeadlinesCount > 0 ? 'red' : 'green'"
          class="mr-2 flex-shrink-0"
        >
          {{ pastDeadlinesCount > 0 ? "mdi-alert" : "mdi-calendar-check" }}
        </v-icon>
        <span class="flex-shrink-0">Taken</span>
        <v-chip
          v-if="deadlines.length > 0"
          size="x-small"
          :color="pastDeadlinesCount > 0 ? 'red-lighten-1' : 'green-lighten-1'"
          variant="tonal"
          class="ml-2 flex-shrink-0"
        >
          {{ deadlines.length }}
        </v-chip>
        <v-spacer />
        <v-btn
          :color="pastDeadlinesCount > 0 ? 'red-darken-1' : 'green-darken-1'"
          variant="text"
          size="small"
          class="flex-shrink-0"
          @click="$emit('addDeadline')"
        >
          <v-icon start>mdi-plus</v-icon>
          Toevoegen
        </v-btn>
      </v-card-title>
      <v-card-text class="pa-0">
        <DeadlineList :deadlines="deadlines" :account_id="account.id" />
      </v-card-text>
    </v-card>

    <template v-if="!showOnlyDeadlines">
      <!-- 2. KEY INFORMATION CARD -->
      <v-card class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-blue-grey-lighten-5">
        <v-icon size="20" color="blue-grey-darken-1" class="mr-2"
          >mdi-information</v-icon
        >
        <span>Uitgebreide informatie</span>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- ID Document Section -->
        <div class="d-flex align-center justify-space-between mb-4">
          <div class="d-flex align-center">
            <v-avatar
              size="48"
              :color="account.id_image ? 'green-lighten-4' : 'grey-lighten-3'"
              class="mr-3"
            >
              <v-icon :color="account.id_image ? 'green-darken-2' : 'grey'">
                {{
                  account.id_image
                    ? "mdi-card-account-details"
                    : "mdi-card-account-details-outline"
                }}
              </v-icon>
            </v-avatar>
            <div>
              <div class="text-body-2 font-weight-medium">Identificatie</div>
              <div class="text-caption text-medium-emphasis">
                {{
                  account.id_image ? "Document beschikbaar" : "Geen document"
                }}
              </div>
            </div>
          </div>
          <v-btn
            :color="account.id_image ? 'primary' : 'grey'"
            variant="tonal"
            size="small"
            :prepend-icon="account.id_image ? 'mdi-eye' : 'mdi-upload'"
            @click="editIdImage"
          >
            {{ account.id_image ? "Bekijk" : "Upload" }}
          </v-btn>
        </div>

        <!-- Key Info Grid - Always show all 4 boxes -->
        <v-row dense class="key-info-grid">
          <v-col cols="6">
            <div class="info-item">
              <div class="d-flex align-center">
                <v-icon size="14" color="grey-darken-1" class="mr-1"
                  >mdi-identifier</v-icon
                >
                <span class="text-caption text-medium-emphasis">BSN</span>
              </div>
              <div class="text-body-2 font-weight-medium mt-1">
                {{ account.legal_number || "N/A" }}
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="info-item">
              <div class="d-flex align-center">
                <v-icon size="14" color="grey-darken-1" class="mr-1"
                  >mdi-barcode</v-icon
                >
                <span class="text-caption text-medium-emphasis">KENO</span>
              </div>
              <div class="text-body-2 font-weight-medium mt-1">
                {{ account.keno_code || "N/A" }}
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="info-item">
              <div class="d-flex align-center">
                <v-icon size="14" color="grey-darken-1" class="mr-1"
                  >mdi-cake-variant</v-icon
                >
                <span class="text-caption text-medium-emphasis">Leeftijd</span>
              </div>
              <div class="text-body-2 font-weight-medium mt-1">
                {{
                  account.date_of_birth
                    ? formatAge(account.date_of_birth)
                    : "N/A"
                }}
              </div>
            </div>
          </v-col>

          <v-col cols="6">
            <div class="info-item">
              <div class="d-flex align-center">
                <v-icon size="14" color="grey-darken-1" class="mr-1"
                  >mdi-earth</v-icon
                >
                <span class="text-caption text-medium-emphasis"
                  >Nationaliteit</span
                >
              </div>
              <div class="text-body-2 font-weight-medium mt-1">
                {{ account.country_name || "N/A" }}
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- 3. TRAITS / KENMERKEN -->
    <v-card v-if="hasTraits" class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-teal-lighten-5">
        <v-icon size="20" color="teal-darken-1" class="mr-2"
          >mdi-tag-heart</v-icon
        >
        <span>Kenmerken</span>
        <v-chip size="x-small" color="teal" class="ml-2">{{
          totalTraits
        }}</v-chip>
      </v-card-title>

      <v-card-text class="pa-3">
        <div v-for="group in groupedTraits" :key="group.name" class="mb-3">
          <div class="text-caption text-medium-emphasis mb-1">
            {{ group.name }}
          </div>
          <div class="d-flex flex-wrap ga-1">
            <v-chip
              v-for="tag in group.tags"
              :key="tag.id"
              :variant="isTraitSelected(tag.id) ? 'elevated' : 'tonal'"
              :color="isTraitSelected(tag.id) ? 'teal' : undefined"
              size="small"
              label
              clickable
              @click="handleTraitClick(tag)"
            >
              <template #prepend>
                <v-icon :color="traitsStore.getTraitColor(tag.id)" size="14">
                  {{ traitsStore.getTraitIcon(tag.id) }}
                </v-icon>
              </template>
              {{ tag.name }}
              <template #append>
                <v-badge
                  :color="getRecencyColor(tag.recency_status)"
                  :content="tag.usage_count"
                  inline
                />
              </template>
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 4. TAGGED CONTACTS -->
    <v-card v-if="hasTaggedContacts" class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-pink-lighten-5">
        <v-icon size="20" color="pink-darken-1" class="mr-2"
          >mdi-account-multiple</v-icon
        >
        <span>Getagde Contacten</span>
        <v-chip size="x-small" color="pink" class="ml-2">{{
          taggedContacts.length
        }}</v-chip>
      </v-card-title>

      <v-card-text class="pa-3">
        <div class="d-flex flex-wrap ga-1">
          <v-chip
            v-for="contact in taggedContacts"
            :key="contact.id"
            :variant="isContactSelected(contact.id) ? 'elevated' : 'tonal'"
            :color="isContactSelected(contact.id) ? 'pink' : undefined"
            size="small"
            label
            clickable
            @click="handleContactClick(contact)"
          >
            <template #prepend>
              <v-icon size="14">mdi-account</v-icon>
            </template>
            {{ contact.name }}
            <template #append>
              <v-badge
                :color="getRecencyColor(contact.recency_status)"
                :content="contact.usage_count"
                inline
              />
            </template>
          </v-chip>
        </div>
      </v-card-text>
    </v-card>

    <!-- 5. NEARBY CONTACTS -->
    <v-card
      class="mb-4 sidebar-card"
      :class="{ 'nearby-card': hasEnrichedLocation }"
    >
      <v-card-text class="pa-3">
        <div class="d-flex align-center">
          <v-avatar
            :color="hasEnrichedLocation ? 'green-lighten-4' : 'grey-lighten-3'"
            size="40"
            class="mr-3"
          >
            <v-icon :color="hasEnrichedLocation ? 'green-darken-2' : 'grey'"
              >mdi-map-marker-radius</v-icon
            >
          </v-avatar>
          <div class="flex-grow-1">
            <div class="text-body-2 font-weight-medium">
              Contacten in de buurt
            </div>
            <div class="text-caption text-medium-emphasis">
              {{
                hasEnrichedLocation
                  ? "Zoek naar andere contacten nabij deze locatie"
                  : "Voeg eerst een locatie toe om te zoeken"
              }}
            </div>
          </div>
          <v-btn
            :color="hasEnrichedLocation ? 'green-darken-2' : 'grey'"
            variant="tonal"
            size="small"
            icon="mdi-magnify"
            :disabled="!hasEnrichedLocation"
            @click="$emit('openNearbyContacts')"
          />
        </div>
      </v-card-text>
    </v-card>

    <!-- 6. LOCATIONS -->
    <v-card class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-blue-lighten-5">
        <v-icon size="20" color="blue-darken-1" class="mr-2"
          >mdi-map-marker</v-icon
        >
        <span>Locaties</span>
        <v-chip v-if="hasLocations" size="x-small" color="blue" class="ml-2">{{
          allLocations.length
        }}</v-chip>
      </v-card-title>

      <v-card-text v-if="hasLocations" class="pa-0">
        <v-list density="compact" class="py-0">
          <v-list-item
            v-for="(ll, index) in allLocations"
            :key="ll.id || index"
            :class="{ 'bg-blue-lighten-5': ll.primary }"
          >
            <template #prepend>
              <v-icon :color="ll.primary ? 'blue' : 'grey'" size="small">
                {{ ll.primary ? "mdi-map-marker" : "mdi-map-marker-outline" }}
              </v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ formatLocationDisplay(ll.location) }}
            </v-list-item-title>
            <v-list-item-subtitle v-if="ll.primary" class="text-caption">
              Primaire locatie
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card-text>

      <!-- No locations state -->
      <v-card-text v-else class="pa-4">
        <div class="d-flex flex-column align-center text-center py-2">
          <v-icon size="40" color="grey-lighten-1" class="mb-2"
            >mdi-map-marker-off</v-icon
          >
          <div class="text-body-2 text-medium-emphasis mb-3">
            Geen locatie bekend
          </div>
          <v-btn
            color="blue"
            variant="tonal"
            size="small"
            prepend-icon="mdi-pencil"
            @click="$emit('edit')"
          >
            Locatie toevoegen
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- 7. STATUS & CATEGORISATION -->
    <v-card class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-purple-lighten-5">
        <v-icon size="20" color="purple-darken-1" class="mr-2"
          >mdi-tag-multiple</v-icon
        >
        <span>Status & Categorisatie</span>
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Category & Lead -->
        <div class="d-flex flex-wrap ga-2 mb-4">
          <v-chip
            v-if="account.account_category"
            color="blue"
            variant="tonal"
            size="small"
            prepend-icon="mdi-shape"
          >
            {{ account.account_category.name }}
          </v-chip>
          <v-chip
            v-if="account.account_lead"
            color="amber-darken-2"
            variant="tonal"
            size="small"
            prepend-icon="mdi-file-sign"
          >
            {{ account.account_lead.name }}
          </v-chip>
        </div>

        <!-- Complexity Indicators -->
        <div class="complexity-grid mb-4">
          <div
            class="complexity-item"
            :class="{ active: account.psychiatrisch }"
          >
            <v-icon
              size="18"
              :color="account.psychiatrisch ? 'red' : 'grey-lighten-1'"
              >mdi-brain</v-icon
            >
            <span class="text-caption">Psychiatrisch</span>
          </div>
          <div class="complexity-item" :class="{ active: account.mz }">
            <v-icon size="18" :color="account.mz ? 'orange' : 'grey-lighten-1'"
              >mdi-layers-triple</v-icon
            >
            <span class="text-caption">Complex</span>
          </div>
          <div
            class="complexity-item"
            :class="{ active: account.complex_long }"
          >
            <v-icon
              size="18"
              :color="account.complex_long ? 'deep-orange' : 'grey-lighten-1'"
              >mdi-calendar-clock</v-icon
            >
            <span class="text-caption">Langdurig</span>
          </div>
          <div
            v-if="account.oggz"
            class="complexity-item"
            :class="{ active: account.oggz === 'Ja' }"
          >
            <v-icon size="18" :color="account.oggz === 'Ja' ? 'red' : 'green'"
              >mdi-clipboard-text</v-icon
            >
            <span class="text-caption">OGGZ</span>
          </div>
        </div>

        <!-- Contact Statistics -->
        <div class="contact-stats">
          <div class="stat-row">
            <div class="stat-item">
              <div class="d-flex align-center justify-center">
                <v-icon size="14" color="blue" class="mr-1"
                  >mdi-calendar-start</v-icon
                >
                <span class="text-caption text-medium-emphasis">Eerste</span>
              </div>
              <div class="text-body-2 mt-1">
                {{ account.first_seen ? formatDate(account.first_seen) : "—" }}
              </div>
            </div>
            <div class="stat-item">
              <div class="d-flex align-center justify-center">
                <v-icon size="14" color="purple" class="mr-1"
                  >mdi-calendar-end</v-icon
                >
                <span class="text-caption text-medium-emphasis">Laatste</span>
              </div>
              <div class="text-body-2 mt-1">
                {{ account.last_seen ? formatDate(account.last_seen) : "—" }}
              </div>
            </div>
            <div class="stat-item">
              <div class="d-flex align-center justify-center">
                <v-icon size="14" color="green" class="mr-1"
                  >mdi-counter</v-icon
                >
                <span class="text-caption text-medium-emphasis">Gezien</span>
              </div>
              <div class="text-body-2 font-weight-medium mt-1">
                {{ account.times_seen || 0 }}×
              </div>
            </div>
          </div>
        </div>

        <!-- Activity Status (Traffic Light) -->
        <div v-if="account.activity_status_code" class="activity-status mt-3">
          <div class="d-flex align-center ga-2">
            <div
              v-for="(a, index) in account.activity_status_code"
              :key="index"
              class="activity-dot"
              :class="{ active: a > 0 }"
              :title="`${formatMonth(index)}: ${a > 0 ? 'Actief' : 'Niet gezien'}`"
            >
              <span class="dot-label">{{ formatMonthThree(index) }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <!-- 8. NOTES CARD (Description, Problem, Actions) -->
    <v-card class="mb-4 sidebar-card">
      <v-card-title class="section-header bg-teal-lighten-5">
        <v-icon size="20" color="teal-darken-1" class="mr-2"
          >mdi-text-box</v-icon
        >
        <span>Notities</span>
      </v-card-title>

      <v-card-text class="pa-4">
        <div class="notes-section mb-3">
          <div class="d-flex align-center mb-1">
            <v-icon size="14" color="grey-darken-1" class="mr-1"
              >mdi-text-box-outline</v-icon
            >
            <span class="text-caption text-medium-emphasis">Omschrijving</span>
          </div>
          <div class="text-body-2 notes-content">
            {{ account.description || "Geen omschrijving" }}
          </div>
        </div>

        <v-divider class="my-3" />

        <div class="notes-section mb-3">
          <div class="d-flex align-center mb-1">
            <v-icon size="14" color="red-darken-1" class="mr-1"
              >mdi-alert-circle-outline</v-icon
            >
            <span class="text-caption text-medium-emphasis">Probleem</span>
          </div>
          <div class="text-body-2 notes-content">
            {{ account.problem || "Geen probleem beschreven" }}
          </div>
        </div>

        <v-divider class="my-3" />

        <div class="notes-section">
          <div class="d-flex align-center mb-1">
            <v-icon size="14" color="green-darken-1" class="mr-1"
              >mdi-checkbox-marked-circle-outline</v-icon
            >
            <span class="text-caption text-medium-emphasis">Acties</span>
          </div>
          <div class="text-body-2 notes-content">
            {{ account.actions || "Geen acties beschreven" }}
          </div>
        </div>
      </v-card-text>
    </v-card>

      <!-- 9. ATTACHMENTS -->
      <AttachmentsBox
        record-type="Account"
        :record-id="account.id"
        :item="account"
        class="mb-4"
      />
    </template>
  </div>
</template>

<script>
import { defineComponent, computed, onMounted } from "vue";
import AttachmentsBox from "../attachments/AttachmentsBox.vue";
import DeadlineList from "../deadlines/DeadlineList.vue";
import moment from "moment";
import { useFormsStore } from "@/stores/formsStore";
import { useTraitsStoreRefactor } from "@/stores/traitsStoreRefactor";
import { useReportFilterStore } from "@/stores/reportFilterStore";

export default defineComponent({
  name: "AccountSidebar",
  components: { AttachmentsBox, DeadlineList },
  props: {
    account: {
      type: Object,
      required: true,
    },
    deadlines: {
      type: Array,
      default: () => [],
    },
    pastDeadlinesCount: {
      type: Number,
      default: 0,
    },
    hasEnrichedLocation: {
      type: Boolean,
      default: false,
    },
    hideDeadlines: {
      type: Boolean,
      default: false,
    },
    showOnlyDeadlines: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["edit", "addDeadline", "openNearbyContacts"],
  setup(props) {
    const formsStore = useFormsStore();
    const traitsStore = useTraitsStoreRefactor();
    const reportFilterStore = useReportFilterStore();

    // Formatting helpers
    const formatDate = (date) => moment(date).format("DD-MM-YYYY");
    const formatMonth = (index) =>
      moment().subtract(index, "months").format("MMMM YYYY");
    const formatMonthThree = (index) =>
      moment().subtract(index, "months").format("MMM");
    const formatAge = (dateOfBirth) => {
      const years = moment().diff(moment(dateOfBirth), "years");
      return `${years} jaar`;
    };

    // Locations
    const allLocations = computed(() => props.account?.linked_locations || []);
    const hasLocations = computed(() => allLocations.value.length > 0);

    const formatLocationDisplay = (location) => {
      if (!location) return "Onbekende locatie";
      if (location.display_name) return location.display_name;
      if (location.street_address && location.city)
        return `${location.street_address}, ${location.city}`;
      if (location.name && location.city)
        return `${location.name}, ${location.city}`;
      return location.name || location.formatted_address || "Onbekende locatie";
    };

    // Tagged Contacts
    const taggedContacts = computed(
      () => props.account?.tagged_contacts_summary || [],
    );
    const hasTaggedContacts = computed(() => taggedContacts.value.length > 0);
    const isContactSelected = (contactId) =>
      reportFilterStore.isContactSelected(contactId);
    const handleContactClick = (contact) =>
      reportFilterStore.toggleContact(contact.id);

    // Traits
    const traits = computed(() => props.account?.tags_summary || []);
    const hasTraits = computed(() => traits.value.length > 0);
    const totalTraits = computed(() => traits.value.length);

    const groupedTraits = computed(() => {
      const byCategoryId = new Map();
      (traits.value || []).forEach((t) => {
        const category =
          traitsStore?.getTraitById?.(t.id)?.category || "Kenmerken";
        if (!byCategoryId.has(category)) byCategoryId.set(category, []);
        byCategoryId.get(category).push(t);
      });
      return Array.from(byCategoryId.entries()).map(([name, arr]) => ({
        name,
        tags: arr,
      }));
    });

    const isTraitSelected = (traitId) =>
      reportFilterStore.isTraitSelected(traitId);
    const handleTraitClick = (trait) => reportFilterStore.toggleTrait(trait.id);

    // Recency color helper
    const getRecencyColor = (status) => {
      if (status === "green") return "success";
      if (status === "yellow") return "warning";
      return "error";
    };

    // ID Image
    const editIdImage = () => {
      formsStore.edit({
        type: "image",
        item: {
          id: props.account.id_image_id || null,
          can_change_image: true,
          file: props.account.id_image,
          file_dl: props.account.id_image_dl,
          recordId: props.account.id,
        },
        options: {
          filetype: "image/png, image/jpeg",
          recordType: "Account",
          field: "id_image_as",
        },
      });
    };

    // Load traits for icons/colors
    onMounted(async () => {
      await traitsStore.fetchAll();
    });

    return {
      // Formatting
      formatDate,
      formatMonth,
      formatMonthThree,
      formatAge,
      // Locations
      allLocations,
      hasLocations,
      formatLocationDisplay,
      // Contacts
      taggedContacts,
      hasTaggedContacts,
      isContactSelected,
      handleContactClick,
      // Traits
      hasTraits,
      totalTraits,
      groupedTraits,
      isTraitSelected,
      handleTraitClick,
      traitsStore,
      // Helpers
      getRecencyColor,
      editIdImage,
    };
  },
});
</script>

<style scoped>
.account-sidebar {
  max-width: 100%;
}

.sidebar-card {
  border-radius: 12px !important;
  overflow: hidden;
}

.section-header {
  font-size: 0.875rem !important;
  font-weight: 500;
  padding: 12px 16px !important;
  min-height: 48px !important;
}

.key-info-grid .info-item {
  display: flex;
  flex-direction: column;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.complexity-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
}

.complexity-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 4px;
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  opacity: 0.4;
  transition:
    opacity 0.2s,
    background 0.2s;
}

.complexity-item.active {
  opacity: 1;
  background: rgba(0, 0, 0, 0.02);
  border-color: rgba(0, 0, 0, 0.1);
}

.contact-stats .stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
}

.contact-stats .stat-item {
  display: flex;
  flex-direction: column;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
  text-align: center;
}

.activity-status {
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
}

.activity-dot {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

.activity-dot::before {
  content: "";
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #ef5350;
  margin-bottom: 4px;
  border: 2px solid #ffcdd2;
}

.activity-dot.active::before {
  background: #4caf50;
  border-color: #c8e6c9;
}

.activity-dot .dot-label {
  font-size: 10px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.nearby-card {
  border: 2px dashed rgba(var(--v-theme-success), 0.3) !important;
  background: rgba(var(--v-theme-success), 0.02);
}

.notes-content {
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 6px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(0, 0, 0, 0.7);
}

.notes-section .notes-content:empty::before,
.notes-content:has(:only-child:empty)::before {
  content: "";
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .complexity-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .contact-stats .stat-row {
    grid-template-columns: 1fr;
  }
}
</style>
